<template lang="pug">
.page__grid
  .page__main
    .glide.mt-3.mt-md-0(v-if="articles && articles.length > 0" ref="glideRef")
      .glide__track(data-glide-el='track')
        ul.glide__slides
          li.glide__slide(v-for='(item, index) in articles' :key='index')
            EcreativMoleculesCardsArticleCard.glide__image(
              :id='item.id',
              :image='item.image',
              :content="item.truncateContent"
              :truncateContent="''"
              :title="item.title"
              :link="item.slug"
              :date="''"
              category='articles'
              linkLabel="Learn More"
              :loading="index < 2 ? 'eager' : 'lazy'"
            )
      .glide__arrows.top-30.w-100.position-absolute(data-glide-el='controls')
        button.glide__arrow.glide__arrow--left(data-glide-dir='<')
          EcreativAtomsSvgIcon(
            iconName='chevronLeft',
            isCustom=true,
            iconSize='md'
          )
        button.glide__arrow.glide__arrow--right(data-glide-dir='>')
          EcreativAtomsSvgIcon(
            iconName='chevronRight',
            isCustom=true,
            iconSize='md'
          )
</template>

<script setup>
import { watch, onMounted, nextTick, onBeforeUnmount, toRefs, ref } from 'vue';
import Glide, {
  Swipe,
  Images,
  Controls,
  Autoplay,
} from '@glidejs/glide/dist/glide.modular.esm.js';
import EcreativMoleculesCardsArticleCard from '../molecules/Cards/ArticleCard.vue';
import EcreativAtomsSvgIcon from '../atoms/SvgIcon.vue';

const props = defineProps({
  articles: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const { articles } = toRefs(props);
const glideRef = ref(null);

let glideInstance = null;

// Initialize Glide.js
const initiateGlide = () => {
  if (typeof window === 'undefined') return;

  nextTick(() => {
    const glideElement = glideRef.value;
    // Ensure the element exists
    if (!glideElement) {
      return;
    }

    // Prevent duplicate initialization
    if (glideInstance) {
      glideInstance.destroy();
    }

    // Initialize a new Glide instance
    glideInstance = new Glide(glideElement, {
      type: 'slider',
      autoplay: 3000,
      perView: 1,
      gap: 0,
    });

    glideInstance.mount({ Swipe, Images, Controls, Autoplay });

    // Signal initialization immediately to show all slides
    const slidesElement = glideElement.querySelector('.glide__slides');
    if (slidesElement) {
      slidesElement.classList.add('glide--init');
    }
  });
};

// Cleanup on component unmount
onBeforeUnmount(() => {
  if (glideInstance) {
    glideInstance.destroy();
    glideInstance = null;
  }
});

// Watch for changes in articles and reinitialize Glide if needed
watch(
  () => articles.value,
  (newVal) => {
    if (newVal?.length > 0) {
      initiateGlide();
    }
  },
  { deep: true, immediate: true },
);

// Initialize Glide when the component is mounted
onMounted(() => {
  if (articles.value?.length > 0) {
    initiateGlide();
  }
});
</script>

<style scoped lang="scss">
/* SSR/Pre-init Safety: Ensure the first slide is visible before Glide mounts */
.glide__slides {
  display: flex;
  overflow: hidden;
  margin: 0;
  padding: 0;
  list-style: none;

  &:not(.glide--init) {
    .glide__slide:not(:first-child) {
      display: none;
    }

    .glide__slide:first-child {
      width: 100%;
    }
  }
}

.page__grid {
  display: grid;
  grid: repeat(1, auto) / 0 1fr 0;
  grid-template-areas: '. main .';
  gap: 2rem 0.5rem;

  @include media-breakpoint-up(sm) {
    grid-template-columns: calc(50vw - 17.1rem) 1fr calc(50vw - 17.1rem);
  }

  @include media-breakpoint-up(md) {
    grid-template-columns: calc(50vw - 22rem) 1fr calc(50vw - 22rem);
  }

  @include media-breakpoint-up(lg) {
    grid-template-areas: '. main main main main main main main main main main main main main main main main main main main main main main main main .';
    grid-template-columns:
      calc(50vw - 30rem)
      repeat(24, 1fr)
      calc(50vw - 30rem);
  }

  @include media-breakpoint-up(xl) {
    grid-template-areas: 'main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main main .';
    grid-template-columns:
      repeat(36, 1fr)
      calc(50vw - 36rem);
    gap: 3rem 1rem;
  }

  @include media-breakpoint-up(xxl) {
    grid-template-columns: repeat(36, 1fr) calc(50vw - 40rem);
  }
}

.page__main {
  grid-area: main;
}
</style>
